import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Maximize2, RotateCw } from 'lucide-react';

export default function DraggableOverlay({ logoSrc, placement, onPlacementChange }) {
  const zoneRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [dragStart, setDragStart] = useState(null);

  const p = useMemo(() => placement || { x: 0, y: 0, width: 100, height: 100, rotation: 0, flipH: false }, [placement]);

  const getPercentPos = useCallback((e) => {
    if (!zoneRef.current) return { x: 0, y: 0 };
    const rect = zoneRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: ((cx - rect.left) / rect.width) * 100,
      y: ((cy - rect.top) / rect.height) * 100,
    };
  }, []);

  // Get raw client position for rotation calculation
  const getClientPos = useCallback((e) => {
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { cx, cy };
  }, []);

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getPercentPos(e);
    setDragStart({ mx: pos.x, my: pos.y, ox: p.x, oy: p.y });
    setDragging(true);
  }, [getPercentPos, p.x, p.y]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getPercentPos(e);
    setDragStart({ mx: pos.x, my: pos.y, ow: p.width, oh: p.height, ox: p.x, oy: p.y, aspectRatio: p.width / p.height });
    setResizing(true);
  }, [getPercentPos, p.width, p.height, p.x, p.y]);

  const handleRotateStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!zoneRef.current) return;
    const rect = zoneRef.current.getBoundingClientRect();
    // Calculate center of the logo element in viewport coords
    const centerX = rect.left + (rect.width * (p.x + p.width / 2)) / 100;
    const centerY = rect.top + (rect.height * (p.y + p.height / 2)) / 100;
    const { cx, cy } = getClientPos(e);
    const startAngle = Math.atan2(cy - centerY, cx - centerX) * (180 / Math.PI);
    setDragStart({ startAngle, startRotation: p.rotation || 0, centerX, centerY });
    setRotating(true);
  }, [getClientPos, p.x, p.y, p.width, p.height, p.rotation]);

  const handleMove = useCallback((e) => {
    if (!dragStart) return;
    e.preventDefault();

    if (rotating) {
      const { cx, cy } = getClientPos(e);
      const currentAngle = Math.atan2(cy - dragStart.centerY, cx - dragStart.centerX) * (180 / Math.PI);
      const delta = currentAngle - dragStart.startAngle;
      let newRotation = (dragStart.startRotation + delta) % 360;
      if (newRotation < 0) newRotation += 360;
      onPlacementChange({ ...p, rotation: Math.round(newRotation) });
      return;
    }

    const pos = getPercentPos(e);
    const dx = pos.x - dragStart.mx;
    const dy = pos.y - dragStart.my;

    if (dragging) {
      const nx = Math.max(0, Math.min(100 - p.width, dragStart.ox + dx));
      const ny = Math.max(0, Math.min(100 - p.height, dragStart.oy + dy));
      onPlacementChange({ ...p, x: Math.round(nx * 10) / 10, y: Math.round(ny * 10) / 10 });
    } else if (resizing) {
      const ar = dragStart.aspectRatio || 1;
      const delta = Math.max(dx, dy);
      const nw = Math.max(15, Math.min(100 - dragStart.ox, dragStart.ow + delta));
      const nh = nw / ar;
      const clampedW = Math.min(nw, 100 - dragStart.ox);
      const clampedH = Math.min(nh, 100 - dragStart.oy);
      const scale = Math.min(clampedW / nw, clampedH / nh);
      const finalW = nw * scale;
      const finalH = nh * scale;
      onPlacementChange({ x: dragStart.ox, y: dragStart.oy, width: Math.round(finalW * 10) / 10, height: Math.round(finalH * 10) / 10, rotation: p.rotation, flipH: p.flipH });
    }
  }, [dragStart, dragging, resizing, rotating, p, onPlacementChange, getPercentPos, getClientPos]);

  const handleEnd = useCallback(() => {
    setDragging(false);
    setResizing(false);
    setRotating(false);
    setDragStart(null);
  }, []);

  useEffect(() => {
    if (dragging || resizing || rotating) {
      const onMove = (e) => handleMove(e);
      const onUp = () => handleEnd();
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend', onUp);
      return () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('touchend', onUp);
      };
    }
  }, [dragging, resizing, rotating, handleMove, handleEnd]);

  const isActive = dragging || resizing || rotating;

  return (
    <div ref={zoneRef} className="absolute inset-0" data-testid="draggable-overlay-zone">
      {/* Logo */}
      <div
        className={`absolute cursor-grab active:cursor-grabbing select-none touch-none group ${dragging ? 'opacity-90' : ''}`}
        style={{ left: `${p.x}%`, top: `${p.y}%`, width: `${p.width}%`, height: `${p.height}%` }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        data-testid="draggable-logo"
      >
        <img
          src={logoSrc}
          alt="Your logo"
          className="w-full h-full object-contain pointer-events-none"
          style={{
            transform: `rotate(${p.rotation || 0}deg) scaleX(${p.flipH ? -1 : 1})`,
            transition: rotating ? 'none' : 'transform 0.2s ease',
          }}
          draggable={false}
        />

        {/* Border when interacting */}
        <div className={`absolute inset-0 border-2 rounded-sm transition-opacity ${isActive ? 'border-[#00E5FF] opacity-100' : 'border-[#00E5FF]/0 group-hover:border-[#00E5FF]/40 opacity-100'}`} />

        {/* Free rotation handle — top-left corner */}
        <div
          className="absolute -top-3 -left-3 w-6 h-6 flex items-center justify-center cursor-alias z-10 opacity-0 group-hover:opacity-100 transition-opacity"
          onMouseDown={handleRotateStart}
          onTouchStart={handleRotateStart}
          data-testid="rotate-handle"
          title="Drag to rotate"
        >
          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shadow-lg transition-colors ${rotating ? 'bg-[#00E5FF] border-[#00E5FF]' : 'bg-[#0A0A0A]/80 border-[#00E5FF] hover:bg-[#00E5FF]'}`}>
            <RotateCw className="w-2 h-2 text-white" />
          </div>
        </div>

        {/* Resize handle — bottom-right corner */}
        <div
          className="absolute -bottom-1.5 -right-1.5 w-5 h-5 flex items-center justify-center cursor-nwse-resize z-10 group/resize"
          onMouseDown={handleResizeStart}
          onTouchStart={handleResizeStart}
          data-testid="resize-handle"
        >
          <div className="w-3 h-3 rounded-sm bg-[#00E5FF] border border-white/50 shadow-lg group-hover/resize:scale-125 transition-transform">
            <Maximize2 className="w-2 h-2 text-white m-auto mt-0.5" />
          </div>
        </div>

        {/* Rotation & size indicator */}
        {isActive && (
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-[#0A0A0A]/80 text-[#00E5FF] text-[9px] font-mono px-1.5 py-0.5 rounded whitespace-nowrap" data-testid="placement-indicator">
            {rotating ? `${Math.round(p.rotation || 0)}°` : `${Math.round(p.width)}% × ${Math.round(p.height)}%`}
          </div>
        )}
      </div>
    </div>
  );
}
